import React, { useEffect, useState } from "react";

/**
 * SendInterviewDialog — ask someone to answer an interview when they have
 * ten minutes (Plan D).
 *
 * Two ways to name them:
 *  - Someone with access (Who can see). They answer from their own account;
 *    someone who can only read the profile gets access to this interview only.
 *  - Someone new (Oct 29). By default, a link to this interview with no
 *    account or password. Ticking "Also invite them as a contributor" sends
 *    one contributor invitation instead, which carries the interview.
 * Either way the result is a link the coordinator passes on — no email is
 * sent from here, and the dialog says so. On success the link is on the
 * clipboard and the button reads "Copied ✓".
 *
 * Wording is a first draft for David.
 */
export default function SendInterviewDialog({ clientId, clientName, interview, onClose, onSent }) {
  const [tab, setTab] = useState("existing");
  const [people, setPeople] = useState(null);
  const [relationships, setRelationships] = useState([]);
  // Setup may already have picked who answers (Plan E3) — start there.
  const [userId, setUserId] = useState(interview?.respondent?.user_id || "");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [contributor, setContributor] = useState(false);
  const [note, setNote] = useState(interview?.respondent_note || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);   // { link, kind, respondent_name }

  useEffect(() => {
    let live = true;
    Promise.all([
      fetch(`/api/clients/${clientId}/access`).then(r => r.ok ? r.json() : []),
      fetch(`/api/clients/${clientId}/interviews/relationships`).then(r => r.ok ? r.json() : []),
    ]).then(([p, r]) => {
      if (!live) return;
      // Anyone with an account who can reach the profile can answer; those who
      // can't add to it get access to this interview only, and the row says
      // so. Interview links (no account) aren't people to send to.
      setPeople((Array.isArray(p) ? p : []).filter(x => !x.is_you && x.user_id));
      setRelationships(Array.isArray(r) ? r : []);
    }).catch(() => { if (live) setError("Couldn't load people."); });
    return () => { live = false; };
  }, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function send() {
    setBusy(true); setError(null);
    try {
      if (note !== (interview?.respondent_note || "")) {
        await fetch(`/api/clients/${clientId}/interviews/${interview.id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ respondent_note: note }),
        });
      }
      const body = tab === "existing" ? { user_id: userId }
        : { name: name.trim(), email: email.trim(), relationship, contributor };
      const r = await fetch(`/api/clients/${clientId}/interviews/${interview.id}/send`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't send that.");
      const full = `${window.location.origin}${d.link}`;
      try { await navigator.clipboard?.writeText(full); } catch { /* shown below anyway */ }
      setResult({ ...d, full });
      onSent?.(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // The contributor invitation needs an email and a relationship it can
  // invite with; a link needs only a name (or an email) to label it.
  const pickable = contributor ? relationships.filter(r => r.contributor) : relationships;
  const relOk = !contributor || pickable.some(r => r.value === relationship);
  const canSend = tab === "existing" ? !!userId
    : contributor ? (email.includes("@") && relOk)
    : !!(name.trim() || email.includes("@"));

  function toggleContributor(on) {
    setContributor(on);
    if (on && !relationships.some(r => r.contributor && r.value === relationship)) {
      setRelationship(relationships.find(r => r.contributor)?.value || "");
    }
  }

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.box} role="dialog" aria-label="Send this interview" onKeyDown={e => e.key === "Escape" && onClose()}>
        <div style={m.header}>
          <h2 style={m.title}>Ask someone about {clientName || "them"}</h2>
          <button style={m.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        {result ? (
          <div style={m.done}>
            <p style={m.doneLine}>
              {result.kind === "link"
                ? `Send this link to ${result.respondent_name || "them"} however you normally reach them. They don't need an account or a password. It works for 14 days, or until the interview is done.`
                : result.kind === "new"
                ? `Send this link to ${result.respondent_name || "them"} however you normally reach them. It works for 7 days. They'll set a password, become a contributor, and go straight to the questions.`
                : `${result.respondent_name || "They"} will see this the next time they sign in. The link is a shortcut.`}
              {result.interview_only && " They can answer this interview, and nothing else in the profile."}
            </p>
            <code style={m.link}>{result.full}</code>
            <div style={m.footer}>
              <button style={m.saveBtn} onClick={onClose}>Copied ✓ — done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={m.tabs} role="tablist">
              <button role="tab" aria-selected={tab === "existing"} style={{ ...m.tab, ...(tab === "existing" ? m.tabOn : {}) }} onClick={() => setTab("existing")}>Someone with access</button>
              <button role="tab" aria-selected={tab === "new"} style={{ ...m.tab, ...(tab === "new" ? m.tabOn : {}) }} onClick={() => setTab("new")}>Someone new</button>
            </div>

            {tab === "existing" && (
              <div style={m.list}>
                {people === null && <p style={m.muted}>Loading…</p>}
                {people && people.length === 0 && <p style={m.muted}>Nobody else has access yet. Try “Someone new”.</p>}
                {(people || []).map(p => {
                  const adds = p.can === "add" || p.can === "build";
                  return (
                    <label key={p.user_id} style={m.person}>
                      <input type="radio" name="who" value={p.user_id}
                             checked={userId === p.user_id} onChange={() => setUserId(p.user_id)} />
                      <span style={m.personName}>{p.display_name}</span>
                      <span style={m.personMeta}>{adds ? p.role_label : `${p.role_label} · they'll get access to answer this interview only`}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {tab === "new" && (
              <div style={m.form}>
                <label style={m.label}>Name
                  <input style={m.input} value={name} onChange={e => setName(e.target.value)} placeholder="Ana" />
                </label>
                <label style={m.label}>Who they are to {clientName || "them"}
                  <select style={m.input} value={relationship} onChange={e => setRelationship(e.target.value)}
                    aria-label="Relationship">
                    {!contributor && <option value="">—</option>}
                    {pickable.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
                <label style={m.check}>
                  <input type="checkbox" checked={contributor} onChange={e => toggleContributor(e.target.checked)} />
                  <span>Also invite them as a contributor</span>
                </label>
                <label style={m.label}>Email {contributor ? "" : "(optional)"}
                  <input style={m.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ana@example.com" />
                </label>
                <p style={m.muted}>
                  {contributor
                    ? "They'll set a password, go straight to these questions, and can add to the profile afterwards. They'll show in Who can see under Can add to it."
                    : "They'll get a link to these questions only — no account, no password. They can come back to it until it's done."}
                </p>
              </div>
            )}

            <label style={m.label}>What to tell them
              <input style={m.input} value={note} onChange={e => setNote(e.target.value)}
                     placeholder="Ten minutes on how Maris does mornings. Thank you." />
            </label>

            <p style={m.muted}>Nothing is emailed from here. You'll get a link to send however you normally reach them.</p>
            {error && <p style={m.error}>{error}</p>}
            <div style={m.footer}>
              <button style={m.cancelBtn} onClick={onClose} disabled={busy}>Cancel</button>
              <button style={{ ...m.saveBtn, ...(!canSend || busy ? m.disabled : {}) }} onClick={send} disabled={!canSend || busy}>
                {busy ? "Working…" : "Get link"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const m = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 },
  box:       { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-6)", width: 520, maxWidth: "100%", display: "flex", flexDirection: "column", gap: "var(--space-4)", maxHeight: "90vh", overflowY: "auto" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  close:     { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  tabs:      { display: "flex", gap: 4, borderBottom: "1px solid var(--color-border)" },
  tab:       { font: "inherit", fontSize: "var(--text-sm)", padding: "8px 12px", border: "none", borderBottom: "2px solid transparent", background: "none", color: "var(--color-text-tertiary)", cursor: "pointer" },
  tabOn:     { color: "var(--color-text-primary)", borderBottom: "2px solid var(--color-accent)", fontWeight: "var(--weight-medium)" },
  list:      { display: "flex", flexDirection: "column", gap: 2, maxHeight: 220, overflowY: "auto" },
  person:    { display: "grid", gridTemplateColumns: "20px 1fr", columnGap: 10, alignItems: "center", padding: "8px 6px", borderRadius: "var(--radius-md)", cursor: "pointer" },
  check:     { display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", cursor: "pointer" },
  personName:{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  personMeta:{ gridColumn: 2, fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  form:      { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  label:     { display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  input:     { font: "inherit", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  muted:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  footer:    { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  cancelBtn: { font: "inherit", fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer" },
  saveBtn:   { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  disabled:  { opacity: 0.5, cursor: "not-allowed" },
  done:      { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  doneLine:  { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.5 },
  link:      { fontSize: "var(--text-xs)", background: "var(--color-bg-subtle)", padding: "8px 10px", borderRadius: "var(--radius-sm)", wordBreak: "break-all" },
};
